// Shared handling for failed data loads: toast + Sentry warning, skipping non-failures.
// The pattern (and why it's a warning, not an error) is documented in the library:
// company/engineering/frontend_patterns/load_errors
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { useToast } from 'vue-toastification'

export const LOAD_ERROR_MESSAGE = "Couldn't load this data."

// Other toasts go through these too, so only this file imports the toast library.
export function showErrorToast(message) {
  useToast().error(message)
}

export function showSuccessToast(message, { toastId } = {}) {
  useToast().success(message, toastId ? { id: toastId } : undefined)
}

export function dismissToast(toastId) {
  useToast().dismiss(toastId)
}

function isSkipped(error, expected404) {
  if (axios.isCancel(error) || error?.name === 'AbortError') return true
  const status = error?.response?.status
  if (status === 401 || status === 403) return true
  return status === 404 && expected404
}

// Returns true when the failure was reported, so the caller knows to show its error state.
// toastOptions customise the toast (e.g. timeout: false to keep it until dismissed).
export function notifyLoadError(
  error,
  { feature, endpoint, expected404 = false, message = LOAD_ERROR_MESSAGE, toastId, toastOptions } = {}
) {
  if (isSkipped(error, expected404)) return false
  console.error(message, error)
  useToast().error(message, { id: toastId ?? `load-error:${feature}`, ...toastOptions })
  Sentry.captureException(error, {
    level: 'warning',
    tags: { load_error: 'true', feature, endpoint },
    fingerprint: ['load-error', feature, endpoint],
  })
  return true
}
